import ModalDefault from "../../../../common/components/modal/modal-default";
import React, {useEffect, useRef, useState} from "react";
import AiProcessing01 from "../../../../assets/images/ai/ai-processing-01.gif";
import AiProcessing02 from "../../../../assets/images/ai/ai-processing-02.gif";
import AiProcessing03 from "../../../../assets/images/ai/ai-processing-03.gif";
import AiProcessing04 from "../../../../assets/images/ai/ai-processing-04.gif";
import FileViewer from "../../../../common/components/file-viewer/components";
import ChatbotBox from "../../../../common/components/layout/layout-components/AIChat/chatbot-box";
import ButtonIcon from "../../../../common/components/button/button-icon";
import ArrowPathIcon from "@heroicons/react/20/solid/ArrowPathIcon";
import {AI_DOCUMENT_PROCESSING_MESSAGES, CDN} from "../../../util/util-consts";
import Button from "../../button";
import {classNames} from "../../../util/util-helpers";

const torqueImageSets = [
    {webp1x: `${CDN}/Torque-01_1x.webp`, webp2x: `${CDN}/Torque-01_2x.webp`, gif1x: `${CDN}/Torque-01_1x.gif`, gif2x: `${CDN}/Torque-01_2x.gif`},
    {webp1x: `${CDN}/Torque-02_1x.webp`, webp2x: `${CDN}/Torque-02_2x.webp`, gif1x: `${CDN}/Torque-02_1x.gif`, gif2x: `${CDN}/Torque-02_2x.gif`},
    {webp1x: `${CDN}/Torque-03_1x.webp`, webp2x: `${CDN}/Torque-03_2x.webp`, gif1x: `${CDN}/Torque-03_1x.gif`, gif2x: `${CDN}/Torque-03_2x.gif`},
];

const oldImageArray = [AiProcessing01, AiProcessing02, AiProcessing03, AiProcessing04];

export default function OcrDocumentModal({
                                             show,
                                             file,
                                             processingDocumentTitle,
                                             onFetchFormData,
                                             onUploadDocument,
                                             onClose,
                                             isPreviewModalOpen,
                                             setIsPreviewModalOpen,
                                             previewContent,
                                             hideDialogFooter,
                                             formModalTitle,
                                             onSubmit,
                                             submitButtonLabel,
                                             htmlAfter,
                                             fileViewerClass,
                                             displayOldAnimation,
                                             footerCustomHtmlLeft,
                                             translate
                                         }) {
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isTimeout, setIsTimeout] = useState(false);
    const dataLoading = AI_DOCUMENT_PROCESSING_MESSAGES;
    const dataLoadingRandomIndex = Math.floor(Math.random() * dataLoading.length);
    const randomOldImage = useRef(oldImageArray[Math.floor(Math.random() * oldImageArray.length)]);
    const randomTorqueSet = useRef(torqueImageSets[Math.floor(Math.random() * torqueImageSets.length)]);

    useEffect(() => {
        setIsUploadModalOpen(show);
        if (show && file) {
            setIsTimeout(false);
            onUploadDocument(file);

            const fileURL = window.URL.createObjectURL(new Blob([file]));

            setPreviewUrl(fileURL);
        }
    }, [show, file]);

    return <>
        <ModalDefault
            show={isUploadModalOpen}
            hideDialogFooter={true}
            widthClass={"max-w-xl"}
            limitHeight={true}
            title={processingDocumentTitle
                ? processingDocumentTitle
                : translate("modal_heading.processing_document")
            }
            closeButtonLabel={translate("btn.close")}
            onClose={() => {
                onClose();
                setIsPreviewModalOpen(false);
                setIsUploadModalOpen(false)
            }}
        >
            <div className='flex items-center justify-center pt-10 px-10'>
                {displayOldAnimation ? (
                    <div className="size-24">
                        <img src={randomOldImage.current} alt=""/>
                    </div>
                ) : (
                    <picture>
                        <source
                            type="image/webp"
                            srcSet={`${randomTorqueSet.current.webp1x} 1x, ${randomTorqueSet.current.webp2x} 2x`}
                        />

                        <source
                            type="image/gif"
                            srcSet={`${randomTorqueSet.current.gif1x} 1x, ${randomTorqueSet.current.gif2x} 2x`}
                        />

                        <img src={randomTorqueSet.current.gif1x} alt="" className="h-40 w-auto"/>
                    </picture>
                )}
            </div>

            <div className="text-base font-semibold py-4 px-12 bg-tm-gray-200 m-4 rounded-l-xl rounded-br-xl">
                <ChatbotBox
                    fakeStreamSpeed={20}
                    streamContent={true}
                    content={dataLoading[dataLoadingRandomIndex]}
                />

                <TimeoutTimer
                    time={180000}
                    isTimeout={isTimeout}
                    onTimeout={() => setIsTimeout(true)}
                />
            </div>

            {isTimeout && (
                <div className="text-base flex flex-wrap items-center bg-yellow-600/10 text-tm-gray-900 py-2 px-4">
                    <span>Processing action is taking longer than expected.</span>

                    <Button
                        onClick={() => {
                            onUploadDocument(file);
                            setIsTimeout(false);
                        }}
                        addClass="ml-auto"
                    >
                        Retry
                    </Button>
                </div>
            )}
        </ModalDefault>

        <ModalDefault
            show={isPreviewModalOpen}
            widthClass={"max-w-screen"}
            addClass={"h-dialog-body"}
            title={formModalTitle}
            hideDialogFooter={hideDialogFooter}
            customHtmlLeft={footerCustomHtmlLeft}
            options={
                <ButtonIcon className="btn-icon p-1" onClick={() => onFetchFormData()}>
                    <ArrowPathIcon className="size-5"/>
                </ButtonIcon>
            }
            closeButtonLabel={translate("btn.close")}
            onClose={() => {
                setIsPreviewModalOpen(false);
                setIsUploadModalOpen(false);
            }}
            onButtonClick={onSubmit}
            buttonLabel={submitButtonLabel}
        >
            <div className='grid grid-cols-12 min-h-full'>
                <div className={classNames(fileViewerClass ?? "sticky top-0 col-span-5 border-r border-tm-gray-300 h-dialog-body")}>
                    <FileViewer
                        fileType={file?.path?.split('.')?.pop()}
                        defaultZoom={1}
                        filePath={previewUrl}
                        onError={(e) => {
                            console.log(e)
                        }}
                    />
                </div>

                <div className="col-span-7 relative">
                    {previewContent}
                </div>
            </div>

            {htmlAfter}
        </ModalDefault>
    </>
}

function TimeoutTimer({time, isTimeout, onTimeout}) {
    const timerRef = useRef(null);

    useEffect(() => {
        if (time <= 0 || isTimeout) return;

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
            onTimeout();
        }, time);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [time, isTimeout, onTimeout]);

    return null;
}